import {
    Show,
    SimpleShowLayout,
    TextField,
    useRecordContext,
    TopToolbar,
    EditButton,
    DeleteButton,
    usePermissions,
    Loading,
    DateField,
    NumberField,
    ArrayField,
    Datagrid,
    FunctionField,
    useRedirect,
    useDataProvider,
    downloadCSV,
    ReferenceField,
    useCreatePath,
    useNotify,
    Labeled,
} from "react-admin";
import { useState } from "react";
import {
    Button,
    Chip,
    Divider,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import SsidChartIcon from '@mui/icons-material/SsidChart';
import { IntegralResultsTable, formatMol } from './channels/IntegralResults';

const InstrumentShowActions = () => {
    const { permissions } = usePermissions();
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [exporting, setExporting] = useState(false);

    if (!record) {
        return null
    }

    const runExport = (method: string, suffix: string) => {
        setExporting(true);
        dataProvider[method]('instruments', record)
            .then(response => {
                const headers = response.data[0];
                const rows = response.data.slice(1);

                let csvContent = headers.join(",") + "\n";
                rows.forEach(row => {
                    csvContent += row.join(",") + "\n";
                });

                // Filename is split at the extension so file.txt becomes file_raw
                const outFilename = record.filename.split('.')[0] + suffix;

                downloadCSV(csvContent, outFilename);
            })
            .catch(error => {
                console.error("Export failed:", error);
                notify('Export failed', { type: 'error' });
            })
            .finally(() => setExporting(false));
    };

    return (
        <TopToolbar>
            {permissions === 'admin' && <>
                <Tooltip title="Measured currents for every channel">
                    <span>
                        <Button variant="outlined" size="small" startIcon={<DownloadIcon />} disabled={exporting} onClick={() => runExport('getInstrumentRawData', '_raw')}>Raw CSV</Button>
                    </span>
                </Tooltip>
                <Tooltip title="Baseline-corrected currents for every channel">
                    <span>
                        <Button variant="outlined" size="small" startIcon={<DownloadIcon />} disabled={exporting} onClick={() => runExport('getInstrumentBaselineFilteredData', '_filtered')}>Filtered CSV</Button>
                    </span>
                </Tooltip>
                <Tooltip title="Per-sample integral results in moles for every channel">
                    <span>
                        <Button variant="outlined" size="small" startIcon={<DownloadIcon />} disabled={exporting} onClick={() => runExport('getInstrumentSummaryData', '_summary')}>Summary CSV</Button>
                    </span>
                </Tooltip>
                <Tooltip title="Edit name, description and project">
                    <span>
                        <EditButton />
                    </span>
                </Tooltip>
                <Tooltip title="Delete this experiment and all its channels">
                    <span>
                        <DeleteButton />
                    </span>
                </Tooltip>
            </>}
        </TopToolbar>
    );
}

const MetadataRow = () => (
    <Stack direction="row" spacing={4} useFlexGap flexWrap="wrap">
        <Labeled>
            <DateField source="date" label="Measured" showTime emptyText="-" />
        </Labeled>
        <Labeled>
            <TextField source="instrument_model" emptyText="-" />
        </Labeled>
        <Labeled>
            <TextField source="device_filename" emptyText="-" />
        </Labeled>
        <Labeled label="Init E">
            <NumberField source="init_e" emptyText="-" />
        </Labeled>
        <Labeled>
            <NumberField source="sample_interval" emptyText="-" />
        </Labeled>
        <Labeled>
            <NumberField source="run_time" emptyText="-" />
        </Labeled>
        <Labeled>
            <NumberField source="quiet_time" emptyText="-" />
        </Labeled>
        <Labeled>
            <NumberField source="sensitivity" emptyText="-" />
        </Labeled>
    </Stack>
);

const ChannelList = () => {
    const record = useRecordContext();
    const { permissions } = usePermissions();
    const redirect = useRedirect();
    const createPath = useCreatePath();
    if (!record) {
        return <Loading />;
    }
    const isAdmin = permissions === 'admin';

    const handleRowClick = (record) => {
        if (!record) {
            return null;
        }

        return createPath({
            resource: 'instrument_channels',
            type: 'show',
            id: record,
        });
    };

    const renderBaseline = (channel) => {
        const points = (channel.baseline_chosen_points ?? []).length;
        const hasBaseline = (channel.baseline_values ?? []).length > 0;
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                    size="small"
                    variant="outlined"
                    color={hasBaseline ? 'success' : 'default'}
                    label={hasBaseline ? `${points} points` : 'Not set'}
                />
                {isAdmin && (
                    <Tooltip title="Edit baseline">
                        <IconButton
                            size="small"
                            onClick={(event) => {
                                event.stopPropagation();
                                redirect('edit', 'instrument_channels', channel.id);
                            }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>
        );
    };

    const renderIntegration = (channel) => {
        const results = (channel.integral_results ?? []).length;
        const hasBaseline = (channel.baseline_values ?? []).length > 0;
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                    size="small"
                    variant="outlined"
                    color={results > 0 ? 'success' : 'default'}
                    label={results > 0 ? `${results} samples` : 'None'}
                />
                {isAdmin && (
                    <Tooltip title={hasBaseline ? 'Pick integration ranges' : 'Set a baseline first'}>
                        <span>
                            <IconButton
                                size="small"
                                disabled={!hasBaseline}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    redirect(`/instrument_channels/${channel.id}/integrate`);
                                }}
                            >
                                <SsidChartIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </Stack>
        );
    };

    const renderSamples = (channel) => {
        const names = (channel.integral_results ?? [])
            .map(result => result.sample_name)
            .filter(Boolean);
        if (names.length === 0) {
            return '-';
        }
        return (
            <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>
                {names.join(', ')}
            </Typography>
        );
    };

    const renderTotal = (channel) => {
        const areas = (channel.integral_results ?? [])
            .map(result => Number(result.area))
            .filter(Number.isFinite);
        if (areas.length === 0) {
            return '-';
        }
        return formatMol(areas.reduce((sum, area) => sum + area, 0));
    };

    // Sort a copy of the channels list by name
    const channels = [...(record.channels ?? [])].sort((a, b) => a.channel_name.localeCompare(b.channel_name));

    return (
        <ArrayField source="channels" record={{ ...record, channels }}>
            <Datagrid
                rowClick={handleRowClick}
                expand={<IntegralResultsTable />}
                isRowExpandable={row => (row.integral_results ?? []).length > 0}
                bulkActionButtons={false}
            >
                <TextField source="channel_name" label="Channel" sortable={false} />
                <FunctionField label="Baseline" render={renderBaseline} />
                <FunctionField label="Integration" render={renderIntegration} />
                <FunctionField label="Samples" render={renderSamples} />
                <FunctionField label="Total [mol]" render={renderTotal} />
            </Datagrid>
        </ArrayField>
    );
}


export const InstrumentShow = () => {
    return (
        <Show actions={<InstrumentShowActions />}>
            <SimpleShowLayout >
                <ReferenceField
                    label="Project"
                    source="project_id"
                    reference="projects"
                    link="show"
                    emptyText="N/A"
                    sortable={false}
                >
                    <TextField source="name" />
                </ReferenceField>
                <TextField source="name" />
                <TextField source="description" />
                <DateField source="last_updated" showTime />
                <TextField source="filename" />
                <MetadataRow />
                <Divider sx={{ my: 1 }} />
                <Typography variant="h6">Channels</Typography>
                <ChannelList />
            </SimpleShowLayout>
        </Show >
    )
};

export default InstrumentShow;
